import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupabaseClient } from '../lib/supabase';
import { getEnvVar } from '../lib/env';
import type { UserProgress } from '../types';

export function useUserProgress(
  entityType: 'course' | 'course_session' | 'material',
  entityId: string
) {
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['user-progress', entityType, entityId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id)
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as UserProgress | null;
    },
    enabled: !!entityId,
  });
}

export function useUpdateProgress() {
  const supabase = getSupabaseClient();
  const queryClient = useQueryClient();
  const apiUrl = getEnvVar('VITE_API_URL');

  return useMutation({
    mutationFn: async (params: {
      entityType: 'course' | 'course_session' | 'material';
      entityId: string;
      progress: number;
      completed?: boolean;
    }) => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${apiUrl}/api/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          entityType: params.entityType,
          entityId: params.entityId,
          progress: params.progress,
          completed: params.completed || params.progress >= 100,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update progress');
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: ['user-progress', variables.entityType, variables.entityId] 
      });
    },
  });
}

// Hook to get overall progress stats for settings page
export function useProgressStats() {
  const supabase = getSupabaseClient();

  return useQuery({
    queryKey: ['user-progress-stats'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { coursesStarted: 0, coursesCompleted: 0, materialsCompleted: 0 };

      // Get all progress records (materials, sessions, courses)
      const { data, error } = await supabase
        .from('user_progress')
        .select('entity_type, entity_id, progress, completed')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching progress stats:', error);
        return { coursesStarted: 0, coursesCompleted: 0, materialsCompleted: 0 };
      }

      // Count unique courses/sessions started (any progress > 0)
      const courseProgress = data?.filter(p => p.entity_type === 'course') || [];
      const sessionProgress = data?.filter(p => p.entity_type === 'course_session') || [];
      const materialProgress = data?.filter(p => p.entity_type === 'material') || [];

      // If no course-level tracking, count sessions/materials as "courses started"
      const coursesStarted = courseProgress.length > 0 
        ? courseProgress.length 
        : new Set([...sessionProgress, ...materialProgress].map(p => p.entity_id)).size > 0 ? 1 : 0;
      
      const coursesCompleted = courseProgress.filter(p => p.completed).length;
      const materialsCompleted = materialProgress.filter(p => p.completed).length;

      return { coursesStarted, coursesCompleted, materialsCompleted };
    },
  });
}

// New hook to check access via Stripe API
export function useStripeAccess(
  entityType: 'course' | 'material',
  entityId: string
) {
  const supabase = getSupabaseClient();
  const apiUrl = getEnvVar('VITE_API_URL');

  return useQuery({
    queryKey: ['stripe-access', entityType, entityId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { hasAccess: false, reason: 'not_authenticated' };

      // Call your backend worker to check Stripe
      const response = await fetch(`${apiUrl}/api/stripe/check-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({
          entityType,
          entityId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to check access');
      }

      return await response.json();
    },
    enabled: !!entityId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes to avoid too many Stripe API calls
  });
}
